/**
 * WorkflowSubworkflowContainer.tsx — Subworkflow 缩进 mini 根行 + 左边界
 *
 * 职责：与 workflow 根同壳行语法；嵌套子树包在 boundary 内；done 折叠为 ExpandTrigger。
 *
 * 链路位置：WorkflowChrome → scope subworkflow 节点。
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { useSessionViewOptions } from '../../core/context/SessionContext';
import type { WorkflowContainerRuntimeNode } from '../../types/workflowContainer';
import { WorkflowContainerLine } from './WorkflowContainerLine';
import { WorkflowExpandTrigger } from './WorkflowExpandTrigger';
import {
  buildWorkflowRootMeta,
  mapContainerUiStatus,
  mapWorkflowRootStatusLabel,
  workflowIndentUnit,
} from './workflowContainerLineUtils';

export interface WorkflowSubworkflowContainerProps {
  node: WorkflowContainerRuntimeNode;
  depth?: number;
  children?: ReactNode;
  /** 子树内仍有 running/pending 容器时为 true，阻止 subworkflow-end 过早折叠 */
  subtreeHasActiveWork?: boolean;
}

function buildDoneSummary(node: WorkflowContainerRuntimeNode): string {
  const display = node.display;
  if (display?.progress_total != null) {
    const total = display.progress_total;
    const current =
      node.status === 'completed' ? total : (display.progress_current ?? total);
    return `${current}/${total} · done`;
  }
  return 'done';
}

export function WorkflowSubworkflowContainer({
  node,
  depth = 1,
  children,
  subtreeHasActiveWork = false,
}: WorkflowSubworkflowContainerProps) {
  const { verbose } = useSessionViewOptions();
  const isRunning = node.status === 'running' || node.status === 'retrying';
  const isDone = node.status === 'completed' || node.status === 'failed';
  const effectivelyRunning = isRunning || subtreeHasActiveWork;
  const effectivelyDone = isDone && !subtreeHasActiveWork;
  const uiStatus = mapContainerUiStatus(
    effectivelyRunning ? 'running' : node.status,
  );
  const testId = `lax-workflow-subworkflow-${node.scope_key}`;
  const teaser = useMemo(() => {
    if (node.content_blocks.length === 0) return '';
    return node.content_blocks[node.content_blocks.length - 1]?.preview ?? '';
  }, [node.content_blocks]);

  const [expanded, setExpanded] = useState(effectivelyRunning);

  useEffect(() => {
    if (effectivelyRunning) setExpanded(true);
    else if (effectivelyDone) setExpanded(false);
  }, [effectivelyRunning, effectivelyDone]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const shellToggle =
    effectivelyRunning ? toggleExpanded
    : effectivelyDone && expanded ? toggleExpanded
      : undefined;

  const depthStyle = {
    '--lax-workflow-depth': depth,
    '--lax-workflow-indent-unit': workflowIndentUnit(depth),
  } as CSSProperties;

  const showShellLine = !(effectivelyDone && !expanded);

  return (
    <div
      className="lax-workflow-subworkflow"
      data-testid={testId}
      data-container-scope="subworkflow"
      style={depthStyle}
    >
      {showShellLine ? (
        <WorkflowContainerLine
          variant="root"
          title={node.title}
          description={node.subtitle || undefined}
          uiStatus={uiStatus}
          meta={buildWorkflowRootMeta(node)}
          statusLabel={mapWorkflowRootStatusLabel(
            effectivelyRunning ? 'running' : node.status,
          )}
          onToggle={shellToggle}
          interactive={effectivelyRunning}
          expanded={expanded}
          titleTestId={`${testId}-title`}
          descriptionTestId={`${testId}-desc`}
          debugScopeKey={node.scope_key}
          verbose={verbose}
        />
      ) : null}

      {expanded && children ? (
        <div
          className="lax-workflow-subworkflow-boundary"
          data-testid={`${testId}-boundary`}
          style={depthStyle}
        >
          {children}
        </div>
      ) : null}

      {!expanded && children ? (
        <WorkflowExpandTrigger
          summary={`${node.title} · ${effectivelyDone ? buildDoneSummary(node) : 'running'}`}
          summaryTestId={`${testId}-title`}
          teaser={teaser || undefined}
          expandHint={effectivelyRunning ? '(click to expand subworkflow)' : undefined}
          onToggle={() => setExpanded(true)}
          expanded={false}
          testId={effectivelyDone ? `${testId}-done` : `${testId}-running-collapsed`}
          teaserTestId={teaser ? `${testId}-teaser` : undefined}
        />
      ) : null}
    </div>
  );
}
